/** @format */

import { useMemo } from "react";
import { hasDisplayableContent, type Brand, type Question, type QuestionNode } from "@question-bank/shared";
import { QuestionContentBlocks } from "./QuestionContentBlocks";
import { toLangCode, type ExportLanguage } from "../utils/language";

interface QuestionPartTreeProps {
	nodes: QuestionNode[];
	brand: Brand;
	activeLang: ExportLanguage;
	selectedPartIds: Set<string>;
	onTogglePart: (part: Question) => void;
	selectPartLabel: (questionNumber: string) => string;
}

interface TreeNodeProps {
	node: QuestionNode;
	depth: number;
	brand: Brand;
	activeLang: ExportLanguage;
	selectedPartIds: Set<string>;
	onTogglePart: (part: Question) => void;
	selectPartLabel: (questionNumber: string) => string;
}

/** Format assessment objectives a part carries marks for (e.g. ["AO1", "AO2"]). */
const formatAOs = (ao?: { ao1?: number; ao2?: number; ao3?: number }): string[] =>
	ao ? (["ao1", "ao2", "ao3"] as const).filter((key) => (ao[key] ?? 0) > 0).map((key) => key.toUpperCase()) : [];

/** Format a specification as a breadcrumb. */
const formatSpec = (spec?: { theme?: string; topic?: string; subTopic?: string }): string =>
	spec ? [spec.theme, spec.topic, spec.subTopic].filter(Boolean).join(" › ") : "";

/**
 * Remove duplicate nodes from a tree. If a node appears both as a nested
 * child and as a top-level sibling (a known data shape), keep only the first
 * occurrence encountered in depth-first order.
 */
const deduplicateTree = (nodes: QuestionNode[]): QuestionNode[] => {
	const seen = new Set<string>();
	const dedup = (list: QuestionNode[]): QuestionNode[] =>
		list.reduce<QuestionNode[]>((acc, node) => {
			if (seen.has(node.id)) {
				return acc;
			}
			seen.add(node.id);
			acc.push({ ...node, children: dedup(node.children ?? []) });
			return acc;
		}, []);
	return dedup(nodes);
};

const TreeNode = ({
	node,
	depth,
	brand,
	activeLang,
	selectedPartIds,
	onTogglePart,
	selectPartLabel,
}: TreeNodeProps) => {
	// Skip root — it is rendered separately above the tree.
	if (node.isRoot) {
		return (
			<>
				{(node.children ?? []).map((child) => (
					<TreeNode
						key={child.id}
						node={child}
						depth={0}
						brand={brand}
						activeLang={activeLang}
						selectedPartIds={selectedPartIds}
						onTogglePart={onTogglePart}
						selectPartLabel={selectPartLabel}
					/>
				))}
			</>
		);
	}

	const isSelected = selectedPartIds.has(node.id);
	const isDisplayable = hasDisplayableContent(node);
	const aos = formatAOs(node.assessmentObjectives);
	const specLabel = formatSpec(node.specification);

	return (
		<div style={{ paddingLeft: `${depth * 1.5}rem` }}>
			{isDisplayable ? (
				<button
					data-testid="question-part-checkbox"
					type="button"
					role="checkbox"
					aria-checked={isSelected}
					aria-label={selectPartLabel(node.questionNumber)}
					onClick={() => onTogglePart(node)}
					className={`flex w-full flex-col gap-3 text-left bg-(--public-color-page-bg) rounded-[10px] p-4 border-2 cursor-pointer ${
						isSelected ? "border-(--public-color-btn-add)" : "border-(--public-color-border-default)"
					}`}
				>
					<div className="flex items-start justify-between gap-2">
						<div className="flex min-w-0 items-center gap-2.5">
							<span className="shrink-0 font-roboto text-[14px] font-bold text-(--public-color-text-filter)">
								{node.questionNumber}
							</span>
							{aos.map((ao) => (
								<span
									key={ao}
									className="shrink-0 flex h-5 items-center rounded-[5px] bg-(--public-color-ao-pill,#6ad1c9) px-2.5 font-lato text-[13px] font-bold"
								>
									{ao}
								</span>
							))}
							{specLabel && (
								<span className="min-w-0 flex-1 font-lato text-[12px] text-(--public-color-text-lighter) truncate mr-3">
									{specLabel}
								</span>
							)}
						</div>
						<span
							aria-hidden="true"
							className={`flex shrink-0 size-6 items-center justify-center rounded-md border-2 ${
								isSelected
									? "border-(--public-color-btn-add) bg-(--public-color-btn-add)"
									: "border-(--public-color-border-default) bg-white"
							}`}
						>
							{isSelected && (
								<svg
									xmlns="http://www.w3.org/2000/svg"
									fill="none"
									viewBox="0 0 24 24"
									strokeWidth={3}
									stroke="currentColor"
									className="size-3.5 text-white"
								>
									<path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
								</svg>
							)}
						</span>
					</div>
					<QuestionContentBlocks
						question={node}
						language={toLangCode(activeLang)}
						brand={brand}
						containerClassName="flex flex-col gap-3"
						textClassName="whitespace-pre-wrap font-roboto text-[14px] leading-6 text-(--public-color-text-default)"
						imageClassName="w-full rounded-md object-contain"
						imageAlt={`Question ${node.questionNumber}`}
					/>
				</button>
			) : (
				<div className="flex w-full flex-col gap-1 rounded-[10px] p-3 border border-(--public-color-border-default) bg-(--public-color-page-bg)">
					<span className="font-roboto text-[14px] font-bold text-(--public-color-text-filter)">
						{node.questionNumber}
					</span>
				</div>
			)}
			{(node.children ?? []).length > 0 && (
				<div className="mt-2 flex flex-col gap-2">
					{(node.children ?? []).map((child) => (
						<TreeNode
							key={child.id}
							node={child}
							depth={depth + 1}
							brand={brand}
							activeLang={activeLang}
							selectedPartIds={selectedPartIds}
							onTogglePart={onTogglePart}
							selectPartLabel={selectPartLabel}
						/>
					))}
				</div>
			)}
		</div>
	);
};

/**
 * Recursive tree renderer for question parts in the public question modal.
 * Each non-root node is rendered with a checkbox and indented by depth level
 * (1.5rem per level). The root is skipped — it is displayed separately above.
 *
 * Deduplicates by id: if the same part appears both nested and at the top
 * level (a known data shape from the API), it is rendered only once.
 */
export const QuestionPartTree = ({
	nodes,
	brand,
	activeLang,
	selectedPartIds,
	onTogglePart,
	selectPartLabel,
}: QuestionPartTreeProps) => {
	const dedupedNodes = useMemo(() => deduplicateTree(nodes), [nodes]);
	return (
		<div className="flex flex-col gap-2">
			{dedupedNodes.map((node) => (
				<TreeNode
					key={node.id}
					node={node}
					depth={0}
					brand={brand}
					activeLang={activeLang}
					selectedPartIds={selectedPartIds}
					onTogglePart={onTogglePart}
					selectPartLabel={selectPartLabel}
				/>
			))}
		</div>
	);
};
